import { Component, Input } from '@angular/core';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import { GeneralHelper } from 'src/app/helpers/general-helper';
import { IPBoardHelper } from 'src/app/helpers/ip-board-helper';
import ProductHelper from 'src/app/helpers/product-helper';
import Company from 'src/app/model/company/company';
import AdjustmentInvoice from 'src/app/model/invoice/adjustment-invoice';
import { PriceLine } from 'src/app/model/price-line';
import IPBoard from 'src/app/model/product/ip-board/ip-board';
import { Adjustment } from 'src/app/model/project/adjustment';
import { Project } from 'src/app/model/project/project';
import { SUB_PROJECT_TYPES } from 'src/app/model/project/sub-project';
import { ProjectDisplayObservableService } from 'src/app/services/project-display-observable.service';
import { SubscriptionBasedComponent } from 'src/app/subscription-helpers/subscription-based.component';
import { GetStandardPriceForImmoSoldRentedControllerRequestService } from 'src/app/services/api/immo/get-standard-price-for-immo-sold-rented/get-standard-price-for-immo-sold-rented-controller-request.service';
import { ImmoBillingDetailSubProject } from 'src/app/pages/immo-ip/overview/billing-detail/model/immo-billing-detail-sub-project';
import { SoldRented } from 'src/app/model/project/sold-rented';
import { StringHelper } from 'src/app/helpers/string-helper';
import { BoardTypeChoices } from 'src/app/model/product/board/board-part';

@Component({
  selector: 'client-adjustment-company-invoice-info',
  templateUrl: './client-adjustment-company-invoice-info.component.html',
  styleUrls: ['./client-adjustment-company-invoice-info.component.scss']
})
export class ClientAdjustmentCompanyInvoiceInfoComponent extends SubscriptionBasedComponent {
  @Input()
  company: Company;
  @Input()
  project: Project;
  @Input()
  adjustment: Adjustment;
  @Input()
  baseProject: ImmoBillingDetailSubProject;
  @Input()
  amountCompanies: number;

  subProjectDescription: string;
  adjustmentInvoice: AdjustmentInvoice;
  toShowPriceLines: PriceLine[];

  stickerProductionPrice: number = 0;

  constructor(
    private projectDisplayObservableService: ProjectDisplayObservableService,
    private getStandardPriceForImmoSoldRentedControllerRequestService: GetStandardPriceForImmoSoldRentedControllerRequestService
  ) {
    super();
  }

  ngOnInit(): void {
    this.calculateSubProjectDescription();
    this.calculateToShowPriceLines();
    this.calculateStickerProductionPrice();

    this.addSubscription(
      this.projectDisplayObservableService.getProjectInvoicePriceChangedSubscribable(),
      () => {
        this.calculateToShowPriceLines();
      },
      () => {},
      500
    );
  }

  private calculateSubProjectDescription(): void {
    if (
      this.project == null ||
      !CollectionHelper.isPopulated(this.project.products)
    ) {
      this.subProjectDescription = null;
      return;
    }

    const ipBoard = <IPBoard>(
      this.project.products.find(p => ProductHelper.isIPBoard(p))
    );

    if (ipBoard == null || ipBoard.boardPart == null) {
      this.subProjectDescription = null;
      return;
    }

    this.subProjectDescription =
      this.getSubProjectDescriptionPrefix() +
      IPBoardHelper.getShortDescription(ipBoard);
  }

  private getSubProjectDescriptionPrefix(): string {
    if (this.adjustment.type === SUB_PROJECT_TYPES.ADJUSTMENT) {
      return 'Aanpassing op ';
    } else if (this.adjustment.type === SUB_PROJECT_TYPES.SOLDRENTED) {
      return 'Sticker op ';
    } else if (this.adjustment.type === SUB_PROJECT_TYPES.REPO) {
      return 'Weghalen ';
    } else {
      return '';
    }
  }

  private calculateToShowPriceLines(): void {
    if (!CollectionHelper.isPopulated(this.adjustment.projectInvoices)) {
      this.toShowPriceLines = [];
      return;
    }

    const adjustmentInvoice = this.getAdjustmentInvoice();
    if (adjustmentInvoice == null) {
      this.toShowPriceLines = [];
      return;
    }

    this.adjustmentInvoice = adjustmentInvoice;

    const toShowPriceLines: PriceLine[] = [];

    if (
      !this.adjustment.invoiceAsNonExecuted &&
      !adjustmentInvoice.onlyChargePriceLines &&
      this.adjustment.type === SUB_PROJECT_TYPES.SOLDRENTED
    ) {
      this.handleAddFixedSoldRentedExecutionPrice(toShowPriceLines);
    }

    if (CollectionHelper.isPopulated(adjustmentInvoice.priceLines)) {
      const toPush = adjustmentInvoice.priceLines.filter(pl => {
        if (pl.price == null) {
          return false;
        }
        const priceNum = Number(pl.price);
        return priceNum !== 0 && GeneralHelper.isNumeric(priceNum);
      });
      toShowPriceLines.push(...toPush);
    }
    this.toShowPriceLines = toShowPriceLines;
  }

  private handleAddFixedSoldRentedExecutionPrice(
    toShowPriceLines: PriceLine[]
  ): void {
    const fixedSoldRentedExecutionPriceLine = new PriceLine();
    fixedSoldRentedExecutionPriceLine.text = 'Aanbrengen sticker';
    fixedSoldRentedExecutionPriceLine.price = '-';
    toShowPriceLines.push(fixedSoldRentedExecutionPriceLine);

    this.getStandardPriceForImmoSoldRentedControllerRequestService
      .getStandardPriceForSoldRented(this.adjustment.id)
      .then((standardPrice: number) => {
        fixedSoldRentedExecutionPriceLine.price = (
          standardPrice / this.amountCompanies
        ).toString();
      });
  }

  private calculateStickerProductionPrice() {
    if (this.adjustment.type === SUB_PROJECT_TYPES.SOLDRENTED) {
      const soldRented = <SoldRented>this.adjustment;
      let basePrice = null;
      if (
        soldRented.signSticker != null &&
        soldRented.signSticker.charge != null &&
        soldRented.signSticker.charge.value &&
        soldRented.signSticker.charge.price > 0
      ) {
        basePrice = soldRented.signSticker.charge.price;
      } else if (
        soldRented.signSticker == null &&
        !StringHelper.isEmptyOrWhiteSpace(soldRented.otherSticker)
      ) {
        basePrice = 10;
      }
      if (basePrice != null) {
        this.stickerProductionPrice =
          (basePrice *
            this.getSecondBoardMultiplier() *
            this.getBoardTypeMultiplier()) /
          this.amountCompanies;
      }
    }
  }

  private getSecondBoardMultiplier(): number {
    return this.baseProject != null &&
      this.baseProject.projectPlacement != null &&
      this.baseProject.projectPlacement.secondBoard != null
      ? 2
      : 1;
  }

  private getBoardTypeMultiplier(): number {
    const ipBoard = <IPBoard>(
      this.project.products.find(p => ProductHelper.isIPBoard(p))
    );
    if (
      ipBoard == null ||
      ipBoard.boardPart == null ||
      ipBoard.boardPart.boardType == null
    ) {
      return 1;
    }
    return ipBoard.boardPart.boardType.ordinal ===
      BoardTypeChoices.DOUBLE_SIDED.ordinal ||
      ipBoard.boardPart.boardType.ordinal === BoardTypeChoices.V.ordinal
      ? 2
      : 1;
  }

  private getAdjustmentInvoice(): AdjustmentInvoice {
    return <AdjustmentInvoice>(
      this.adjustment.projectInvoices.find(
        pi => pi.company.id === this.company.id
      )
    );
  }
}
